import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";

import {
  useAccounts,
  useSearchEmails,
  SearchQueryParams,
  useEmailPreview,
  buildAttachmentDownloadUrl,
  useSystemConfig,
  useSystemStats
} from "../api/hooks";
import { apiClient, extractApiError, API_BASE_URL } from "../api/client";
import { useSettingsStore } from "../store/settingsStore";
import { buildEmailDownloadUrl } from "../utils/links";

interface FormState {
  subject: string;
  sender: string;
  recipient: string;
  account: string;
  folder: string;
  dateFrom: string;
  dateTo: string;
}

const defaultForm: FormState = {
  subject: "",
  sender: "",
  recipient: "",
  account: "",
  folder: "",
  dateFrom: "",
  dateTo: ""
};

export function SearchCenter() {
  const { data: accountsData, isLoading: accountsLoading } = useAccounts();
  const configQuery = useSystemConfig();
  const systemStatsQuery = useSystemStats();
  const searchDefaultRows = useSettingsStore((state) => state.searchDefaultRows);
  const [form, setForm] = useState<FormState>(defaultForm);
  const [submittedFilters, setSubmittedFilters] = useState<FormState | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(searchDefaultRows);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTarget, setPreviewTarget] = useState<{ account: string; date: string; folder: string; filename: string } | null>(null);

  useEffect(() => {
    setRowsPerPage(searchDefaultRows);
  }, [searchDefaultRows]);

  const hasFilters = useMemo(() => {
    return Object.values(form).some((value) => value.trim().length > 0);
  }, [form]);

  const selectedAccountFolders = useMemo(() => {
    if (!form.account) {
      return [] as string[];
    }

    const folders = new Set<string>();

    const accountConfig = (configQuery.data?.accounts ?? []).find((entry) => entry.username === form.account);
    for (const folder of accountConfig?.folders ?? []) {
      const value = String(folder).trim();
      if (value) {
        folders.add(value);
      }
    }

    const accountStats = (systemStatsQuery.data?.accounts ?? []).find((entry) => entry.name === form.account);
    for (const folder of Object.keys(accountStats?.folders ?? {})) {
      const value = String(folder).trim();
      if (value) {
        folders.add(value);
      }
    }

    return Array.from(folders).sort((a, b) => a.localeCompare(b));
  }, [form.account, configQuery.data?.accounts, systemStatsQuery.data?.accounts]);

  useEffect(() => {
    if (!form.account || !form.folder) {
      return;
    }
    if (!selectedAccountFolders.includes(form.folder)) {
      setForm((prev) => ({ ...prev, folder: "" }));
    }
  }, [form.account, form.folder, selectedAccountFolders]);

  const searchParams = useMemo<SearchQueryParams>(() => {
    if (!submittedFilters) {
      return { limit: rowsPerPage, offset: page * rowsPerPage };
    }
    return {
      subject: submittedFilters.subject || undefined,
      sender: submittedFilters.sender || undefined,
      recipient: submittedFilters.recipient || undefined,
      account: submittedFilters.account || undefined,
      folder: submittedFilters.folder || undefined,
      date_from: submittedFilters.dateFrom || undefined,
      date_to: submittedFilters.dateTo || undefined,
      limit: rowsPerPage,
      offset: page * rowsPerPage
    };
  }, [submittedFilters, page, rowsPerPage]);

  const { data, isFetching, isError, error, isLoading } = useSearchEmails(
    searchParams,
    Boolean(submittedFilters)
  );

  const results = data?.results ?? [];
  const total = data?.total ?? 0;
  const previewQuery = useEmailPreview(
    previewTarget?.account,
    previewTarget?.date,
    previewTarget?.folder,
    previewTarget?.filename,
    previewOpen
  );

  const downloadWithAuth = async (url: string, suggestedName?: string) => {
    try {
      let requestUrl = url;
      if (url.startsWith(API_BASE_URL)) {
        requestUrl = url.substring(API_BASE_URL.length);
        if (!requestUrl.startsWith("/")) {
          requestUrl = "/" + requestUrl;
        }
      }
      const response = await apiClient.get(requestUrl, { responseType: "blob" });
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = suggestedName ?? "";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Download failed", err);
      alert("Download non riuscito: " + extractApiError(err));
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAccountChange = (event: SelectChangeEvent) => {
    const account = event.target.value;
    setForm((prev) => ({
      ...prev,
      account,
      folder: account ? prev.folder : ""
    }));
  };

  const handleFolderChange = (event: SelectChangeEvent) => {
    setForm((prev) => ({ ...prev, folder: event.target.value }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasFilters) {
      setValidationError("Imposta almeno un filtro per avviare la ricerca.");
      return;
    }
    setValidationError(null);
    setPage(0);
    setSubmittedFilters({ ...form });
  };

  const handlePreview = (entry: typeof results[number]) => {
    setPreviewTarget({
      account: entry.account,
      date: entry.date,
      folder: entry.email.folder,
      filename: entry.email.filename
    });
    setPreviewOpen(true);
  };

  const handleReset = () => {
    setForm(defaultForm);
    setSubmittedFilters(null);
    setPage(0);
    setRowsPerPage(searchDefaultRows);
    setValidationError(null);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      <Card component="form" onSubmit={handleSubmit}>
        <CardHeader
          title="Ricerca avanzata"
          subheader="Imposta uno o più filtri per interrogare l'archivio"
        />
        <CardContent>
          <Stack spacing={2}>
            {validationError && <Alert severity="warning">{validationError}</Alert>}

            <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
              <Grid size={{ xs: 1, md: 6 }}>
                <TextField
                  label="Oggetto"
                  name="subject"
                  value={form.subject}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 1, md: 3 }}>
                <TextField
                  label="Mittente"
                  name="sender"
                  value={form.sender}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 1, md: 3 }}>
                <TextField
                  label="Destinatario"
                  name="recipient"
                  value={form.recipient}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
              <Grid size={{ xs: 1, md: form.account ? 3 : 4 }}>
                <FormControl fullWidth disabled={accountsLoading}>
                  <InputLabel id="account-filter-label">Account</InputLabel>
                  <Select
                    labelId="account-filter-label"
                    label="Account"
                    value={form.account}
                    onChange={handleAccountChange}
                  >
                    <MenuItem value="">
                      <em>Tutti</em>
                    </MenuItem>
                    {(accountsData?.accounts ?? []).map((account) => (
                      <MenuItem key={account.name} value={account.name}>
                        {account.label ?? account.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {form.account ? (
                <Grid size={{ xs: 1, md: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel id="folder-filter-label">Cartella</InputLabel>
                    <Select
                      labelId="folder-filter-label"
                      label="Cartella"
                      value={form.folder}
                      onChange={handleFolderChange}
                    >
                      <MenuItem value="">
                        <em>Tutte</em>
                      </MenuItem>
                      {selectedAccountFolders.map((folder) => (
                        <MenuItem key={folder} value={folder}>
                          {folder}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ) : null}
              <Grid size={{ xs: 1, md: form.account ? 3 : 4 }}>
                <TextField
                  label="Data da"
                  name="dateFrom"
                  type="date"
                  value={form.dateFrom}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 1, md: form.account ? 3 : 4 }}>
                <TextField
                  label="Data a"
                  name="dateTo"
                  type="date"
                  value={form.dateTo}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button startIcon={<RestartAltRoundedIcon />} onClick={handleReset}>
                Reset
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SearchRoundedIcon />}
                disabled={isFetching && submittedFilters !== null}
              >
                Cerca
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="Risultati"
          subheader={submittedFilters ? `Totale risultati: ${total}` : "Esegui una ricerca per visualizzare i risultati"}
          action={isFetching ? <CircularProgress size={20} /> : null}
        />
        <Divider />
        <CardContent>
          {!submittedFilters && (
            <Alert severity="info">Imposta i filtri e premi "Cerca" per interrogare l'archivio.</Alert>
          )}

          {submittedFilters && isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {submittedFilters && !isLoading && isError && (
            <Alert severity="error">Ricerca non riuscita: {extractApiError(error)}</Alert>
          )}

          {submittedFilters && !isLoading && !isError && results.length === 0 && (
            <Alert severity="info">Nessun messaggio trovato con i filtri selezionati.</Alert>
          )}

          {submittedFilters && !isLoading && !isError && results.length > 0 && (
            <Paper sx={{ width: "100%", overflow: "hidden" }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Oggetto</TableCell>
                      <TableCell>Mittente</TableCell>
                      <TableCell>Destinatari</TableCell>
                      <TableCell>Cartella</TableCell>
                      <TableCell align="right">Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((entry) => (
                      <TableRow key={`${entry.account}-${entry.email.uid}-${entry.email.folder}`} hover>
                        <TableCell>{entry.account}</TableCell>
                        <TableCell>{entry.date}</TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          {entry.email.subject || "(Senza oggetto)"}
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>{entry.email.sender}</TableCell>
                        <TableCell sx={{ minWidth: 200 }}>{entry.email.to}</TableCell>
                        <TableCell>{entry.email.folder}</TableCell>
                        <TableCell align="right">
                          <ButtonGroup size="small" variant="outlined">
                          <Button onClick={() => handlePreview(entry)}>Anteprima</Button>
                          <Button
                            startIcon={<DownloadRoundedIcon />}
                            onClick={() => downloadWithAuth(buildEmailDownloadUrl(entry.account, entry.date, entry.email), entry.email.filename)}
                          >
                            Scarica
                          </Button>
                          </ButtonGroup>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </Paper>
          )}
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Anteprima email</DialogTitle>
        <DialogContent dividers>
          {previewQuery.isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : previewQuery.isError ? (
            <Alert severity="error">Impossibile caricare l'anteprima.</Alert>
          ) : previewQuery.data ? (
            <Stack spacing={1}>
              <Typography variant="subtitle1" fontWeight={700}>
                {previewQuery.data.subject || "(Senza oggetto)"}
              </Typography>
              <Divider />
              <Typography variant="body2">
                <strong>Da:</strong> {previewQuery.data.sender || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>A:</strong> {previewQuery.data.to || "-"}
              </Typography>
              {previewQuery.data.cc ? (
                <Typography variant="body2">
                  <strong>CC:</strong> {previewQuery.data.cc}
                </Typography>
              ) : null}
              <Typography variant="body2">
                <strong>Data:</strong> {previewQuery.data.date || "-"}
              </Typography>
              <Divider />
              {previewQuery.data.html ? (
                <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1, bgcolor: "background.paper" }}>
                  <Typography variant="caption" color="text.secondary">
                    Corpo HTML
                  </Typography>
                  <Box sx={{ mt: 1, maxHeight: 400, height: 400, bgcolor: "#fff" }}>
                    <iframe
                      title="preview-html"
                      srcDoc={`<!doctype html><html><head><meta charset="utf-8"/><style>body{margin:0;padding:12px;font-family:Arial,sans-serif;background:#fff;color:#111;} a{color:#1976d2;}</style></head><body>${previewQuery.data.html}</body></html>`}
                      style={{ border: "none", width: "100%", height: "100%" }}
                      sandbox="allow-same-origin"
                    />
                  </Box>
                </Box>
              ) : null}
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {previewQuery.data.body || "(Corpo non disponibile)"}
              </Typography>
              {previewQuery.data.attachments && previewQuery.data.attachments.length > 0 ? (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Allegati</Typography>
                  {previewQuery.data.attachments.map((att) => (
                    <Stack key={att.id} direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {att.filename} {att.size ? `(${(att.size / 1024).toFixed(1)} KB)` : ""}
                      </Typography>
                      {previewTarget ? (
                        <Button
                          size="small"
                          onClick={() =>
                            downloadWithAuth(
                              buildAttachmentDownloadUrl(
                                previewTarget.account,
                                previewTarget.date,
                                previewTarget.folder,
                                previewTarget.filename,
                                att.id
                              ),
                              att.filename
                            )
                          }
                        >
                          Scarica
                        </Button>
                      ) : null}
                    </Stack>
                  ))}
                </Stack>
              ) : null}
            </Stack>
          ) : (
            <Alert severity="info">Seleziona un messaggio per vedere l'anteprima.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
